import React, { DetailedHTMLProps, InputHTMLAttributes, useMemo } from "react";

import classnames from "classnames";

import { useWombatTheme } from "../../theme";
import { CloseIcon } from "../../../icons";

export type BadgeVariant =
  | "primary"
  | "secondary"
  | "ghost"
  | "danger"
  | "warning"
  | "success"
  | "help"
  | "accent";

export type BadgeAppearance = "base" | "solid";
export type BadgeScale = "sm" | "base";

export type BadgeProps = {
  /**
   * Variant
   * @default "primary"
   */
  variant?: BadgeVariant;
  /**
   * Appearance
   * @default "base"
   */
  appearance?: BadgeAppearance;
  /**
   * Scale
   * @default "base"
   */
  scale?: BadgeScale;
  /**
   * Will infer the data-test-id to: badge_${id}
   * @default false
   */
  id?: string;
  /**
   * Max value, only have an impact if children is a number
   * Append a '+' at the end if the value is overpassed
   * @default undefined
   */
  maxValue?: number;
  /**
   * Removable allow badge to be removed
   * @default false
   */
  removable?: boolean;
  /**
   * onRemove, callback called when the Badge is removed
   * Note: This is called only if removable is set to true
   * @default undefined
   */
  onRemove?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
} & DetailedHTMLProps<InputHTMLAttributes<HTMLDivElement>, HTMLDivElement>;

const Badge: React.FC<BadgeProps> = ({
  variant = "primary",
  appearance = "base",
  scale = "base",
  className,
  children,
  maxValue,
  removable = false,
  onRemove,
  id,
  ...props
}) => {
  const { theme } = useWombatTheme();

  const badgeStyle = classnames(
    theme.badge.base[scale],
    theme.badge.variant[appearance][variant]
  );
  const badgeSpacing = classnames(
    theme.badge.spacing[scale][removable ? "removable" : "base"],
    className
  );
  const removeButtonStyle = classnames(
    theme.badge.removable.button[appearance][variant],
    theme.badge.spacing[scale].button
  );
  const removeIconStyle = classnames(
    theme.badge.removable.icon[appearance][variant]
  );
  const numericLabel = useMemo(() => {
    if (maxValue && typeof children === "number") {
      return children > maxValue ? `${maxValue}+` : children.toString();
    }
  }, [maxValue, children]);

  return (
    <div
      id={id}
      className={badgeStyle}
      {...props}
      data-test-id={id ? `badge_${id}` : undefined}
    >
      <div className={badgeSpacing}>
        {numericLabel ? numericLabel : children}
      </div>
      {removable && (
        <button className={removeButtonStyle} onClick={onRemove} data-test-id={id ? `remove_badge_${id}` : undefined}>
          <CloseIcon className={removeIconStyle} />
        </button>
      )}
    </div>
  );
};

export default React.memo(Badge);
